// oxlint-disable max-statements
import { useEffect, useLayoutEffect, useMemo } from 'react';

import { useMachine, useSelector } from '@xstate/react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import { type StateValueFrom, waitFor } from 'xstate';

import { useKnownAvailableParams, useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData } from '../../pageData';
import { VARIANT_ACTOR_IDS, createLogic } from '../orchestrationLogic';
import type { StateContext } from '../types';
import { assertNavigationEvent, buildSnapshot } from '../utils';

import { useInitialDraft } from './useInitialDraft';
import { useSubscribeToMachine } from './useSubscribeToMachine';

type Matches = (value: unknown) => boolean;

/** The orchestrator's `jobs` value: each background operation's region state, e.g. `{ inferScopeLocation: 'running' }`. */
type JobsValue = Extract<Extract<StateValueFrom<ReturnType<typeof createLogic>>, object>['jobs'], object>;
type OperationName = keyof JobsValue;

type StateOptions = {
	currentDraft?: StateContext['draft'];
	onRemount: (draft: StateContext['draft']) => void;
};
export function useStartAPetitionState({ currentDraft, onRemount }: StateOptions) {
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const { pathname, search } = useLocation();
	const { step } = useKnownAvailableParams<{ step: string }>();
	/**
	 * The URL `:step` param normalized for analytics (hyphens → underscores, lowercased),
	 * e.g. `personal-story` → `personal_story`. Use this as the `step` property on
	 * step-level tracking events (`sap_step_view`, `sap_step_submit`) so they always
	 * reflect the step actually being rendered instead of a hardcoded name.
	 */
	const normalizedStep = useMemo(() => step.replace(/-/g, '_').toLowerCase(), [step]);
	const pageData = useStartAPetitionPageData();
	const utilityContext = useUtilityContext();
	const track = useTracking();

	const savedDraft = useInitialDraft(pageData.data.draft);
	const initialDraft = currentDraft ?? savedDraft;

	// `buildSnapshot()` deliberately returns `SnapshotFrom<any>` (see its own oxlint-disable) since
	// the actor tree is dynamic per flow variant and can't be typed against a single concrete
	// machine; that untyped snapshot then flows into `useMachine`'s `snapshot` option below.
	// oxlint-disable-next-line typescript/no-unsafe-assignment, typescript/no-unsafe-return
	const restoredSnapshot = useMemo(() => buildSnapshot(initialDraft, step), [initialDraft, step]);

	const machine = createLogic({ utilityContext, track, featureConfigs: pageData.data.featureConfigs });
	const [state, send, actorRef] = useMachine(machine, {
		// oxlint-disable-next-line typescript/no-unsafe-assignment
		snapshot: restoredSnapshot,
	});

	const children = useSelector(actorRef, (s) => s.children);

	const activeChildRef = VARIANT_ACTOR_IDS.map((id) => children[id]).find(Boolean);
	const snapshot = useSelector(activeChildRef, (s) => s);
	const draft = useSelector(activeChildRef, (s) => s?.context.draft);

	// oxlint-disable-next-line typescript/no-unsafe-type-assertion - We know that the snapshot will have a matches function, but it can't be typed correctly due to the dynamic nature of child actors.
	const matches = (value: NonNullable<typeof snapshot>['value']) =>
		(snapshot as unknown as { matches: Matches } | undefined)?.matches(value);

	/** Checks a background operation's status, e.g. `matchesOperation('inferScopeLocation', 'running')`. */
	const matchesOperation = <NAME extends OperationName>(name: NAME, status: NonNullable<JobsValue[NAME]>) =>
		state.matches({ jobs: { [name]: status } });

	/** Resolves once a background operation isn't running: it's done, failed, or never started. */
	const waitForOperation = async (name: OperationName) => {
		await waitFor(actorRef, (current) => !current.matches({ jobs: { [name]: 'running' } }));
	};

	useSubscribeToMachine(activeChildRef);

	/**
	 * Handles synchronizing the state machine with the URL
	 * when the user navigates using the browser's back/forward buttons
	 */
	useEffect(() => {
		const stepFromSnapshot = snapshot?.value;

		// if the user navigates using the browser's back/forward buttons,
		// we need to ensure the state machine is synchronizes the machine with the URL
		if (navigationType === NavigationType.Pop && stepFromSnapshot !== step) {
			onRemount(draft);
		}
	}, [navigationType, step, navigationType, onRemount, activeChildRef]);

	/**
	 * Handles synchronizing the URL with the state machine
	 * when transitions occur within the machine (e.g. user clicks "Next" button)
	 */
	useLayoutEffect(() => {
		const subscription = actorRef.system.inspect((event) => {
			try {
				assertNavigationEvent(event);
				// oxlint-disable-next-line typescript/no-unsafe-assignment
				const currentSnapshot = event.actorRef.getSnapshot();
				// oxlint-disable-next-line typescript/no-unsafe-argument, typescript/no-unsafe-member-access
				if (Object.keys(currentSnapshot?.children ?? {}).length !== 0) return;

				const targetStep = event._transitions[0]?.target[0]?.key;
				if (typeof targetStep !== 'string') return;

				const pathnameWithoutStep = pathname.split('/').slice(0, -1).join('/');
				navigate(`${pathnameWithoutStep}/${targetStep}${search}`);
			} catch {
				// do nothing if the event is not a navigation event
			}
		});

		return () => subscription.unsubscribe();
	}, [pathname, search, navigate, actorRef]);

	return {
		actions: { send, matches, matchesOperation, waitForOperation },
		data: { draft, normalizedStep },
	};
}
